import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rideburaq_token';
const TIERS = [
  { name: 'Member', min: 0 },
  { name: 'Gold', min: 5000 },
  { name: 'Platinum', min: 15000 },
];

const BuraqMiles = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { navigate('/login'); return; }
    fetch(`${API}/api/auth/me/miles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [navigate]);

  const year = data?.yearMiles || 0;
  const next = TIERS.find((t) => t.min > year);
  const toNext = next ? next.min - year : 0;
  const curMin = [...TIERS].reverse().find((t) => year >= t.min)?.min || 0;
  const pct = next ? Math.min(100, Math.round(((year - curMin) / (next.min - curMin)) * 100)) : 100;
  const redeemValue = data ? Math.floor((data.balance || 0) / (data.redeemRate?.miles || 500)) * (data.redeemRate?.value || 15) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <button data-testid="miles-back-btn" onClick={() => navigate('/dashboard')} className="text-sm text-gray-400 hover:text-white mb-8">← Back</button>
        <p className="text-[11px] tracking-[0.35em] uppercase text-orange-400 mb-4">Buraq Miles</p>

        {loading ? (
          <div className="h-40 rounded-3xl bg-white/5 animate-pulse" />
        ) : !data ? (
          <p className="text-gray-400">We couldn't load your miles right now. Please try again later.</p>
        ) : (
          <>
            <div data-testid="miles-card" className="rounded-3xl border border-orange-500/25 bg-gradient-to-br from-orange-500/[0.10] to-white/[0.02] p-8 mb-8">
              <div className="flex items-end justify-between mb-1">
                <span className="text-sm text-gray-400">Your balance</span>
                <span data-testid="miles-tier" className="text-xs tracking-[0.2em] uppercase text-orange-400">{data.tier}</span>
              </div>
              <div data-testid="miles-balance" className="text-5xl font-semibold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {data.balance.toLocaleString()} <span className="text-lg text-gray-400 font-normal">miles</span>
              </div>
              {redeemValue > 0 && (
                <p className="text-sm text-orange-300/90 mb-6">That's up to ${redeemValue} off your next rides.</p>
              )}
              <div className="mb-2 flex items-center justify-between text-xs text-gray-400">
                <span>{year.toLocaleString()} earned this year</span>
                {next ? <span>{toNext.toLocaleString()} to {next.name}</span> : <span>Top tier reached</span>}
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <h2 className="text-lg mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Your {data.tier} benefits</h2>
            <ul data-testid="miles-benefits" className="space-y-2 mb-8">
              {(data.benefits || []).map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                  <span className="text-orange-500 mt-0.5">•</span><span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-gray-400 leading-relaxed">
              Earn 1 mile for every $1 of fare (tips don't earn). {data.redeemRate?.miles || 500} miles = ${data.redeemRate?.value || 15} off,
              stackable, applied at booking. Miles are a thank-you, not cash — they can't be withdrawn.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuraqMiles;
